import { useLayoutEffect, useState } from 'react';
import { flushSync } from 'react-dom';

interface Params {
	value: number;
	trackRef: React.RefObject<HTMLDivElement | null>;
}

const THUMB_WIDTH = 18;

/**
 * Calculates the `translateX` offset value in px for the slider thumb.
 * Necessary, because we want our slider thumb to:
 *   - Have its left side aligned with the left side of the first marker at `0`.
 *   - Have its right side aligned with the right side of the last marker at `100`.
 *   - Have its center aligned with the center of any mark in between.
 * So we gotta do this hacky `translateX` stuff.
 */
export default function useThumbOffset({ value, trackRef }: Params) {
	const [trackWidth, setTrackWidth] = useState(0);

	const translateXPx = getTranslateX(value, THUMB_WIDTH, trackWidth);

	useLayoutEffect(() => {
		if (!trackRef?.current) {
			return;
		}

		const trackEl = trackRef.current;

		// Without `flushSync` there may be a flash of styles before the track width
		// is set. Noticed when switching between trade / chart tab on mobile.
		// Uses `queueMicrotask` because `flushSync` can't be called during render.
		queueMicrotask(() => {
			flushSync(() => setTrackWidth(trackEl.clientWidth));
		});

		const resizeObserver = new ResizeObserver(() => {
			flushSync(() => setTrackWidth(trackEl.clientWidth));
		});

		resizeObserver.observe(trackEl);

		return () => {
			if (trackEl) {
				resizeObserver.unobserve(trackEl);
			}

			resizeObserver.disconnect();
		};
	}, []);

	return translateXPx;
}

function getTranslateX(
	percent: number,
	thumbWidth: number,
	trackWidth: number
) {
	// If track width is `0` we shouldn't show anything, but adding just in case.
	if (!trackWidth) {
		return -(thumbWidth / 2);
	}

	const halfThumbWidth = thumbWidth / 2;

	// Percentage of movement required for the thumb to no longer be stuck at an edge.
	// We add an extra buffer to smooth the transition a bit, which creates our boundary.
	const halfThumbWidthPercent = (halfThumbWidth / trackWidth) * 100;
	const boundary = halfThumbWidthPercent + 1;

	// Handle left edge. Gradually transition left-aligned <-> centered.
	if (percent <= boundary) {
		const transitionProgress = percent / boundary;
		const offset = -halfThumbWidth * transitionProgress;

		// Extra px because the mark has corners that will stick out of
		// the heavily rounded thumb if we don't cover them completely.
		return offset - 1;
	}

	// Handle right edge. Gradually transition centered <-> right-aligned.
	const rightBoundaryStart = 100 - boundary;

	if (percent >= rightBoundaryStart) {
		const transitionProgress =
			(percent - rightBoundaryStart) / (100 - rightBoundaryStart);
		const offset = -halfThumbWidth * (1 + transitionProgress);

		// Extra px because the mark has corners that will stick out of
		// the heavily rounded thumb if we don't cover them completely.
		return offset + 1;
	}

	// For anything in between, the thumb should just be centered.
	return -halfThumbWidth;
}
